import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { ColumnEvent } from "primereact/column";
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { selectAnswer, selectChecklistReviewRows, selectFilteredReviewRows, selectFailedEdits } from "../../store/selectors/answerSelectors";
import { selectChecklistLoading, selectChecklistError, selectQuestionMap } from "../../store/selectors/checklistSelectors";
import { ReactNode, useCallback, useEffect, useMemo } from "react";
import { setFilteredReviewRows, setFilters, setPage, setSort } from "../../store/slices/answerSlice";
import { reviewAnswerColumns, reviewGlobalFilterFields } from "../../utils/reviewTableHelper";
import { ChecklistReviewRow } from "../../types/reviewAnswer";
import { ReviewTableHeader } from "./ReviewTableHeader";
import { fetchChecklist } from "../../store/thunks/checklistThunks";
import { saveReviewAnswer } from "../../store/thunks/answerThunks";

// Editing is locked once the inspection reaches one of these — mirrors the
// backend's lock set so we don't offer an editor whose save would 409.
const LOCKED_STATUSES = new Set(["full_report", "cancelled", "deleted"]);

// PrimeReact reports newValue===value for an untouched editor; multi-choice
// values are arrays, so also treat an unchanged selection (same ids, any
// order) as a no-op.
const byId = (x: unknown, y: unknown): number => String(x).localeCompare(String(y));

const sameSelection = (a: unknown, b: unknown): boolean =>
    Array.isArray(a) && Array.isArray(b)
    && a.length === b.length
    && [...a].sort(byId).join(" ") === [...b].sort(byId).join(" ");

type Props = {
    /** Optional action(s) forwarded to the header row, right-aligned. */
    headerActions?: ReactNode;
};

export const AnswerTable = ({ headerActions }: Props = {}) => {
    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();
    const { loading, filters, sort, page, selectedInspection } = useAppSelector(selectAnswer);
    const rows = useAppSelector(selectChecklistReviewRows);
    const filteredRows = useAppSelector(selectFilteredReviewRows);
    const failedEdits = useAppSelector(selectFailedEdits);
    const checklistLoading = useAppSelector(selectChecklistLoading);
    const checklistError = useAppSelector(selectChecklistError);
    const questionMap = useAppSelector(selectQuestionMap);

    // Fetch checklist config when inspection changes — the checklist drives the
    // table rows (structure, ordering and unanswered questions).
    useEffect(() => {
        if (selectedInspection) {
            const inspection = selectedInspection as Record<string, unknown>;
            const checklistType = inspection.checklistType as string;
            const checklistVersion = inspection.checklistVersion as string;

            if (checklistType) {
                dispatch(fetchChecklist(checklistType, checklistVersion || "latest"));
            }
        }
    }, [dispatch, selectedInspection]);

    const onPage = useCallback((event: DataTableStateEvent) => {
        dispatch(setPage({ row: event.rows, first: event.first }));
    }, [dispatch]);

    const onFilter = useCallback((event: DataTableStateEvent) => {
        dispatch(setFilters(event.filters));
    }, [dispatch]);

    // Sort is controlled so the slice can revert a section-column sort to
    // checklist order when the section filter is cleared.
    const onSort = useCallback((event: DataTableStateEvent) => {
        dispatch(setSort({ sortField: event.sortField, sortOrder: event.sortOrder }));
    }, [dispatch]);

    const onValueChange = useCallback((value: ChecklistReviewRow[]) => {
        dispatch(setFilteredReviewRows(value));
    }, [dispatch]);

    // Flag rows whose last save failed (kept optimistically) and highlight
    // unanswered questions.
    const rowClassName = useCallback(
        (row: ChecklistReviewRow) => [
            // Edits are keyed by dataDictionaryId (legacy) or questionId (config).
            failedEdits.includes(row.dataDictionaryId ?? row.questionId) ? "review-answer-failed" : "",
            row.response === "" ? "unanswered-question" : "",
        ].filter(Boolean).join(" "),
        [failedEdits]
    );

    // Responses are editable until the inspection reaches a locked status.
    const inspectionId = selectedInspection?.id;
    const editable = !!selectedInspection && !LOCKED_STATUSES.has(selectedInspection.status ?? "");

    // Save a single review-copy edit (the thunk does the optimistic update +
    // failed-flag + conditional cascade). Config-driven rows have no
    // dataDictionaryId, so they are identified by questionId instead.
    const saveEdit = useCallback((row: ChecklistReviewRow, value: unknown) => {
        if (!inspectionId) return;
        const update = row.dataDictionaryId
            ? { dataDictionaryId: row.dataDictionaryId, value, questionType: row.questionType }
            : { questionId: row.questionId, value, questionType: row.questionType };
        dispatch(saveReviewAnswer(inspectionId, update));
    }, [dispatch, inspectionId]);

    // Blur-commit for free-form types. Boolean/single-choice save on selection
    // (onCommit) instead, so skip them here to avoid a redundant second save.
    const onCellEditComplete = useCallback((event: ColumnEvent) => {
        const row = event.rowData as ChecklistReviewRow;
        if (row.questionType === "boolean" || row.questionType === "single-choice") return;
        // An untouched editor reports newValue === value (the formatted response,
        // PrimeReact's field default); also guard the raw value and, for
        // multi-choice, an unchanged array selection.
        if (event.newValue === event.value
            || event.newValue === row.rawValue
            || sameSelection(event.newValue, row.rawValue)) return;
        saveEdit(row, event.newValue);
    }, [saveEdit]);

    // Boolean/single-choice commit on selection rather than waiting for blur.
    const onCommit = useCallback((row: ChecklistReviewRow, value: unknown) => {
        saveEdit(row, value);
    }, [saveEdit]);

    // Re-send a failed row's current (kept) value — no re-entry needed.
    const onRetry = useCallback((row: ChecklistReviewRow) => {
        saveEdit(row, row.rawValue);
    }, [saveEdit]);

    const columns = useMemo(
        () => reviewAnswerColumns(t, { editable, questionMap, onCellEditComplete, failedDataDictionaryIds: failedEdits, onRetry, onCommit }),
        [t, editable, questionMap, onCellEditComplete, failedEdits, onRetry, onCommit]
    );
    // Clone so PrimeReact's in-place cell-edit mutations don't corrupt the
    // memoized selector output: RTK's weakMap cache returns the same rows by
    // reference, so a re-render that doesn't change reviewAnswers (e.g. flagging
    // a failed edit) would otherwise show the mutated row. Same guard as DefectTable.
    const value = useMemo(() => cloneDeep(rows), [rows]);
    const rowOptions = useMemo(() => [50, 75, 100, 150, 250], []);
    const isLoading = loading || checklistLoading;

    return (
        <>
            {checklistError && rows.length === 0 && (
                <div className="p-2 mb-2 border-round surface-warning text-warning-700" style={{ fontSize: '0.85rem' }}>
                    <i className="pi pi-exclamation-triangle mr-2" />
                    {t("answers.checklistLoadError")}
                </div>
            )}
            <ReviewTableHeader filteredCount={filteredRows.length} totalCount={rows.length} actions={headerActions} />
            <DataTable
                value={value}
                dataKey="questionId"
                loading={isLoading}
                filters={filters}
                globalFilterFields={reviewGlobalFilterFields}
                onFilter={onFilter}
                sortField={sort.sortField}
                sortOrder={sort.sortOrder}
                onSort={onSort}
                onValueChange={onValueChange}
                filterDisplay="row"
                editMode="cell"
                paginator
                scrollable
                removableSort
                stripedRows
                size="small"
                first={page.first}
                rows={page.row}
                rowsPerPageOptions={rowOptions}
                onPage={onPage}
                rowClassName={rowClassName}
            >
                {columns}
            </DataTable>
        </>
    );
};
